/**
 * Product Reviews JavaScript.
 * Fetches reviews from Reviews.io API and renders them in the slider.
 */

class ProductReviews {
  constructor() {
    this.config = this.getConfig();
    this.slider = document.querySelector('.js-product-reviews-carousel');
    this.loadingElement = document.querySelector('.js-reviews-loading');
    this.errorElement = document.querySelector('.js-reviews-error');
    this.emptyElement = document.querySelector('.js-reviews-empty');
    this.lightbox = document.querySelector('.js-reviews-lightbox');

    if (this.config && this.slider) {
      this.init();
    } else {
      console.error('Missing required elements for ProductReviews');
    }
  }

  /**
   * Get configuration from the data-reviews-config script tag.
   * @returns {Object|null} The configuration object or null if not found.
   */
  getConfig() {
    const configElement = document.querySelector('.js-reviews-config');
    if (!configElement) {
      console.error('Product reviews config not found');
      return null;
    }

    try {
      const configText = configElement.textContent;
      if (!configText) {
        console.error('Config element has no text content');
        return null;
      }
      const config = JSON.parse(configText);
      return config;
    } catch (error) {
      console.error('Error parsing product reviews config:', error);
      return null;
    }
  }

  /**
   * Initialise the reviews system.
   */
  async init() {
    try {
      // Show loading state.
      this.showLoading();

      // Fetch reviews from API.
      const result = await this.fetchReviews();
      const reviews = result.reviews;
      const apiResponse = result.apiResponse;

      if (reviews && reviews.length > 0) {
        // Filter reviews by minimum rating.
        const filteredReviews = this.filterReviewsByRating(reviews);

        if (filteredReviews.length > 0) {
          // Render filtered reviews.
          this.renderReviews(filteredReviews);

          // Update overall rating if enabled.
          this.updateOverallRating(filteredReviews, apiResponse);

          // Initialise slider.
          this.initSlider();

          // Trigger fade-in animations for the overall rating and carousel.
          this.fadeInElements();

          // Initialise lightbox event listeners after reviews are rendered.
          this.initLightbox();
        } else {
          this.showEmpty();
        }
      } else {
        this.showEmpty();
      }
    } catch (error) {
      console.error('ProductReviews: Error initialising product reviews:', error);
      this.showError();
    }
  }

  /**
   * Filter reviews by minimum rating.
   * @param {Array} reviews - Array of review objects.
   * @returns {Array} Filtered array of reviews.
   */
  filterReviewsByRating(reviews) {
    if (!this.config || !this.config.min_rating) {
      return reviews;
    }

    const minRating = parseInt(this.config.min_rating, 10);

    const filteredReviews = reviews.filter((review) => {
      const rating = parseInt(review.rating, 10) || 0;
      return rating >= minRating;
    });

    return filteredReviews;
  }

  /**
   * Fetch reviews from Reviews.io API.
   * @returns {Promise<Object>} Object containing reviews array and full API response.
   */
  async fetchReviews() {
    if (!this.config) {
      throw new Error('No configuration available');
    }

    const params = new URLSearchParams({
      store: this.config.store || '',
      sku: this.config.sku || '',
      type: this.config.type || 'product_review',
      per_page: String(this.config.per_page || 40)
    });

    const url = `https://api.reviews.io/reviews?${params.toString()}`;

    const response = await fetch(url);

    if (!response.ok) {
      const errorText = await response.text();
      console.error('ProductReviews: API Error Response:', errorText);
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();

    return {
      reviews: data.reviews || [],
      apiResponse: data
    };
  }

  /**
   * Render reviews as slider slides.
   * @param {Array} reviews - Array of review objects.
   */
  renderReviews(reviews) {
    if (!this.slider) {
      console.error('Slider element not found');
      return;
    }

    // Clear existing content.
    this.slider.innerHTML = '';

    reviews.forEach((review, index) => {
      const slide = this.createReviewSlide(review, index);
      if (slide && this.slider) {
        this.slider.appendChild(slide);
      }
    });
  }

  /**
   * Create a single review slide.
   * @param {Object} review - The review object.
   * @param {number} index - The review index.
   * @returns {HTMLElement|null} The created slide element or null if failed.
   */
  createReviewSlide(review, index) {
    if (!review) return null;

    const slide = document.createElement('swiper-slide');
    slide.className = 'product-reviews__slide';
    slide.setAttribute('data-review-id', review.id || `review-${index}`);

    // Create the review card container.
    const reviewCard = document.createElement('div');
    reviewCard.className = 'product-reviews__card';

    // Create verified badge if review is verified.
    if (review.verified) {
      const verifiedBadge = document.createElement('div');
      verifiedBadge.className = 'product-reviews__verified-badge body-xs';

      const badgeIcon = document.createElement('div');
      badgeIcon.className = 'product-reviews__verified-badge-icon svg-wrapper svg-wrapper--xsmall';
      badgeIcon.innerHTML =
        '<svg viewBox="0 0 8 7" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="m1 4.37 1.742 1.874 4.258-5" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';

      verifiedBadge.appendChild(badgeIcon);

      const verifiedText = document.createElement('span');
      verifiedText.textContent = 'Verified Customer';
      verifiedBadge.appendChild(verifiedText);

      reviewCard.appendChild(verifiedBadge);
    }

    // Create rating section.
    const ratingSection = document.createElement('div');
    ratingSection.className = 'product-reviews__rating-section';

    // Add stars.
    const starsContainer = this.createStarRatingHtml(review.rating || 0);
    starsContainer.className = 'product-reviews__card-stars';
    ratingSection.appendChild(starsContainer);

    // Add product title if available.
    if (review.product_name) {
      const productTitle = document.createElement('div');
      productTitle.className = 'body-2xs product-reviews__product-title';
      productTitle.textContent = review.product_name;
      ratingSection.appendChild(productTitle);
    }

    reviewCard.appendChild(ratingSection);

    // Create review title.
    const reviewTitle = document.createElement('div');
    reviewTitle.className = 'heading-xl product-reviews__review-title';

    // Handle title and comments logic.
    let titleContent = '';
    if (review.title && review.comments) {
      titleContent = review.title;
    } else if (review.title && !review.comments) {
      titleContent = review.title;
    } else if (!review.title && review.comments) {
      // Truncate comments to 12 words for title.
      const words = review.comments.trim().split(/\s+/);
      titleContent = words.length <= 12 ? review.comments : words.slice(0, 12).join(' ') + '...';
    } else {
      titleContent = '\u00A0'; // Non-breaking space.
    }

    reviewTitle.textContent = titleContent;
    reviewCard.appendChild(reviewTitle);

    // Create content wrapper.
    const contentWrapper = document.createElement('div');
    contentWrapper.className = 'product-reviews__content-wrapper';

    // Create content section to group text and toggle button.
    const contentSection = document.createElement('div');
    contentSection.className = 'product-reviews__content-section';

    // Create review content.
    const reviewContent = document.createElement('div');
    reviewContent.className = 'body-sm product-reviews__content';

    let contentText = '';
    if (review.title && review.comments) {
      contentText = review.comments;
    } else if (review.title && !review.comments) {
      contentText = review.title;
    } else if (!review.title && review.comments) {
      contentText = review.comments;
    } else {
      contentText = '\u00A0'; // Non-breaking space.
    }

    reviewContent.textContent = contentText;
    contentSection.appendChild(reviewContent);

    // Add show/hide functionality for long content.
    // Check if content actually exceeds 4 lines by measuring height.
    if (contentText && contentText.trim().length > 50 && contentText !== '\u00A0') {
      // Use requestAnimationFrame to ensure the element is rendered.
      requestAnimationFrame(() => {
        this.addShowHideToggle(reviewContent, contentSection);
      });
    }

    contentWrapper.appendChild(contentSection);

    // Create footer.
    const footer = document.createElement('div');
    footer.className = 'product-reviews__footer body-sm';

    // Add images if available.
    if (review.photos && review.photos.length > 0) {
      const imagesContainer = document.createElement('div');
      imagesContainer.className = 'product-reviews__images';

      const imagesInnerContainer = document.createElement('div');
      imagesInnerContainer.className = 'product-reviews__images-container';

      review.photos.forEach((photoUrl, photoIndex) => {
        const image = document.createElement('img');
        image.src = photoUrl;
        image.alt = `Review photo ${photoIndex + 1}`;
        image.className = 'product-reviews__image js-lightbox-trigger';
        image.loading = 'lazy';
        image.setAttribute('data-image-src', photoUrl);
        image.setAttribute('data-image-alt', `Review photo ${photoIndex + 1}`);
        image.title = 'Click to view larger';
        imagesInnerContainer.appendChild(image);
      });

      imagesContainer.appendChild(imagesInnerContainer);
      footer.appendChild(imagesContainer);
    }

    // Add author name if available.
    if (review.author?.name) {
      const authorSpan = document.createElement('span');
      authorSpan.className = 'font-semibold product-reviews__author';
      authorSpan.textContent = review.author.name;
      footer.appendChild(authorSpan);
    }

    // Add location if available.
    if (review.author?.location && review.author.location.trim().length > 0 && review.author.location !== '""') {
      const locationSpan = document.createElement('span');
      locationSpan.className = 'product-reviews__location';
      locationSpan.textContent = `, ${review.author.location}`;
      footer.appendChild(locationSpan);
    }

    // Add date.
    const dateSpan = document.createElement('span');
    dateSpan.className = 'body-2xs product-reviews__date';
    dateSpan.textContent = review.time_ago || review.date_created || '';
    footer.appendChild(dateSpan);

    contentWrapper.appendChild(footer);
    reviewCard.appendChild(contentWrapper);

    slide.appendChild(reviewCard);
    return slide;
  }

  /**
   * Create star rating HTML.
   * @param {number} rating - The rating value (1-5).
   * @returns {HTMLElement} The stars container element.
   */
  createStarRatingHtml(rating) {
    const starsContainer = document.createElement('div');
    starsContainer.className = 'product-reviews__stars';

    for (let i = 1; i <= 5; i++) {
      const star = document.createElement('span');
      star.className = 'product-reviews__star';

      if (i <= rating) {
        star.classList.add('product-reviews__star-color');
        star.innerHTML =
          '<div class="svg-wrapper svg-wrapper--xsmall"><svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.36 2.073a.72.72 0 0 1 1.28 0l1.53 3.002a.68.68 0 0 0 .499.362l3.324.53c.58.091.81.802.396 1.217l-2.38 2.386a.68.68 0 0 0-.19.586l.525 3.33a.719.719 0 0 1-1.036.753l-3-1.528-.074-.032a.678.678 0 0 0-.468 0l-.075.032-2.999 1.528a.719.719 0 0 1-1.036-.752l.526-3.331a.68.68 0 0 0-.19-.586l-2.38-2.386a.719.719 0 0 1 .395-1.218l3.324-.53a.68.68 0 0 0 .5-.361l1.528-3.002Z" fill="currentColor"/></svg></div>';
      } else {
        star.classList.add('product-reviews__star-color');
        star.innerHTML =
          '<div class="svg-wrapper svg-wrapper--xsmall"><svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.36 2.073a.72.72 0 0 1 1.28 0l1.53 3.002a.68.68 0 0 0 .499.362l3.324.53c.58.091.81.802.396 1.217l-2.38 2.386a.68.68 0 0 0-.19.586l.525 3.33a.719.719 0 0 1-1.036.753l-3-1.528-.074-.032a.678.678 0 0 0-.468 0l-.075.032-2.999 1.528a.719.719 0 0 1-1.036-.752l.526-3.331a.68.68 0 0 0-.19-.586l-2.38-2.386a.719.719 0 0 1 .395-1.218l3.324-.53a.68.68 0 0 0 .5-.361l1.528-3.002Z" stroke="currentColor" stroke-width=".8" stroke-linejoin="round"/></svg></div>';
      }

      starsContainer.appendChild(star);
    }

    return starsContainer;
  }

  /**
   * Update overall rating display.
   * @param {Array} reviews - Array of review objects.
   * @param {Object} apiResponse - The full API response object.
   */
  updateOverallRating(reviews, apiResponse) {
    if (!reviews || reviews.length === 0) return;

    const ratingElement = document.querySelector('.js-reviews-rating');
    const countElement = document.querySelector('.js-reviews-count');

    if (ratingElement && countElement) {
      const totalRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0);
      const averageRating = (totalRating / reviews.length).toFixed(1);

      ratingElement.textContent = averageRating;

      // Use the total review count from the API response, not just the returned reviews.
      const totalReviewCount = apiResponse?.review_count || reviews.length;
      countElement.textContent = String(totalReviewCount);

      // Update the review count text for proper grammar.
      const reviewTextElement = document.querySelector('.js-reviews-text');
      if (reviewTextElement) {
        reviewTextElement.textContent = totalReviewCount === 1 ? 'review' : 'reviews';
      }

      // Update Google structured data with actual review information.
      this.updateStructuredData(averageRating, totalReviewCount, reviews);

      // Update star display dynamically based on the actual rating.
      const starsDisplay = document.querySelector('.js-reviews-overall-stars');

      if (starsDisplay) {
        // Find and clear only the star spans, not the entire container.
        const starSpans = starsDisplay.querySelectorAll('.svg-wrapper');
        starSpans.forEach((span) => span.remove());

        // Create the correct number of filled and half-filled stars based on the actual rating.
        for (let i = 1; i <= 5; i++) {
          const star = document.createElement('span');
          star.className = 'svg-wrapper svg-wrapper--small product-reviews__star-color';

          const rating = parseFloat(averageRating);

          if (i <= Math.floor(rating)) {
            // Fully filled star.
            star.innerHTML = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.36 2.073a.72.72 0 0 1 1.28 0l1.53 3.002a.68.68 0 0 0 .499.362l3.324.53c.58.091.81.802.396 1.217l-2.38 2.386a.68.68 0 0 0-.19.586l.525 3.33a.719.719 0 0 1-1.036.753l-3-1.528-.074-.032a.678.678 0 0 0-.468 0l-.075.032-2.999 1.528a.719.719 0 0 1-1.036-.752l.526-3.331a.68.68 0 0 0-.19-.586l-2.38-2.386a.719.719 0 0 1 .395-1.218l3.324-.53a.68.68 0 0 0 .5-.361l1.528-3.002Z" fill="currentColor"/></svg>`;
          } else if (i === Math.ceil(rating) && rating % 1 !== 0) {
            // Half-filled star (for decimal ratings).
            star.innerHTML = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.35938 2.07263C7.62608 1.54974 8.37392 1.54974 8.64062 2.07263L10.1699 5.07458C10.2686 5.26809 10.4544 5.40272 10.6689 5.43689L13.9932 5.96619C14.5724 6.05844 14.8029 6.76868 14.3887 7.18396L12.0088 9.5697C11.8556 9.72329 11.7847 9.94128 11.8184 10.1556L12.3438 13.4867C12.4348 14.0662 11.8305 14.5046 11.3076 14.2386L8.30859 12.7113L8.23438 12.6791C8.08307 12.6234 7.91695 12.6234 7.76562 12.6791L7.69141 12.7113L4.69238 14.2386C4.16953 14.5046 3.5652 14.0662 3.65625 13.4867L4.18164 10.1556C4.21528 9.94133 4.14442 9.72332 3.99121 9.5697L1.61133 7.18396C1.19715 6.76868 1.42761 6.05844 2.00684 5.96619L5.33105 5.43689C5.54558 5.40271 5.73136 5.2681 5.83008 5.07458L7.35938 2.07263Z" stroke="currentColor" stroke-width="0.8" stroke-linejoin="round"/><path d="M8.00035 13.0371C7.95682 13.0371 7.9133 13.0471 7.87339 13.0674L4.87437 14.5947C4.06036 15.0093 3.11894 14.3263 3.26109 13.4238L3.78648 10.0938C3.80037 10.0055 3.77042 9.91576 3.70738 9.85254L1.32749 7.46582C0.683242 6.81942 1.04238 5.71498 1.94371 5.57129L5.2689 5.04199C5.35707 5.02783 5.43343 4.97214 5.47398 4.89258L7.00328 1.8916C7.21086 1.48431 7.6056 1.28014 8.00035 1.28027V13.0371Z" fill="#CA3800"/></svg>`;
          } else {
            // Empty star.
            star.innerHTML = `<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.36 2.073a.72.72 0 0 1 1.28 0l1.53 3.002a.68.68 0 0 0 .499.362l3.324.53c.58.091.81.802.396 1.217l-2.38 2.386a.68.68 0 0 0-.19.586l.525 3.33a.719.719 0 0 1-1.036.753l-3-1.528-.074-.032a.678.678 0 0 0-.468 0l-.075.032-2.999 1.528a.719.719 0 0 1-1.036-.752l.526-3.331a.68.68 0 0 0-.19-.586l-2.38-2.386a.719.719 0 0 1 .395-1.218l3.324-.53a.68.68 0 0 0 .5-.361l1.528-3.002Z" stroke="currentColor" stroke-width=".8" stroke-linejoin="round"/></svg>`;
          }

          starsDisplay.appendChild(star);
        }
      }
    }
  }

  /**
   * Update Google structured data with actual review information.
   * @param {string} averageRating - The average rating value.
   * @param {number} reviewCount - The total number of reviews.
   * @param {Array} reviews - Array of review objects.
   */
  updateStructuredData(averageRating, reviewCount, reviews) {
    // Only update if we have valid review data.
    if (!averageRating || !reviewCount || reviewCount === 0) {
      return;
    }

    // Find the existing structured data script.
    const structuredDataScript = document.querySelector('.js-product-reviews-schema');
    if (!structuredDataScript) {
      console.error('ProductReviews: Structured data script not found');
      return;
    }

    try {
      // Parse existing structured data.
      const scriptText = structuredDataScript.textContent;
      if (!scriptText) {
        console.error('ProductReviews: Structured data script has no content');
        return;
      }
      const structuredData = JSON.parse(scriptText);

      // Add aggregate rating only when we have valid data.
      structuredData.aggregateRating = {
        '@type': 'AggregateRating',
        ratingValue: parseFloat(averageRating),
        reviewCount: parseInt(String(reviewCount), 10),
        bestRating: 5,
        worstRating: 1
      };

      // Add individual reviews (limit to first 10 for performance).
      if (reviews && reviews.length > 0) {
        const reviewData = reviews.slice(0, 10).map((review) => ({
          '@type': 'Review',
          reviewRating: {
            '@type': 'Rating',
            ratingValue: parseInt(review.rating, 10) || 0,
            bestRating: 5,
            worstRating: 1
          },
          author: {
            '@type': 'Person',
            name: review.author?.name || 'Anonymous'
          },
          reviewBody: review.comments || review.title || '',
          datePublished: review.date_created || review.time_ago || ''
        }));

        structuredData.review = reviewData;
      }

      // Update the script content.
      structuredDataScript.textContent = JSON.stringify(structuredData, null, 2);
    } catch (error) {
      console.error('ProductReviews: Error updating structured data:', error);
    }
  }

  /**
   * Initialise the Swiper slider.
   */
  initSlider() {
    if (this.slider && this.slider['initialise'] && typeof this.slider['initialise'] === 'function') {
      this.slider['initialise']();
    }
  }

  /**
   * Show loading state.
   */
  showLoading() {
    if (this.loadingElement && this.loadingElement instanceof HTMLElement) {
      this.loadingElement.style.display = 'block';
    }
    if (this.errorElement && this.errorElement instanceof HTMLElement) {
      this.errorElement.style.display = 'none';
    }
    if (this.emptyElement && this.emptyElement instanceof HTMLElement) {
      this.emptyElement.style.display = 'none';
    }

    // Show the overall rating column and carousel when loading, but keep them hidden initially.
    const overallRating = document.querySelector('.js-reviews-overall');
    if (overallRating && overallRating instanceof HTMLElement) {
      overallRating.style.display = 'block';
      // Keep it hidden initially, will fade in later.
      overallRating.classList.remove('is-visible');
    }

    const carousel = document.querySelector('.js-reviews-carousel');
    if (carousel && carousel instanceof HTMLElement) {
      // Only set display block if carousel is not in empty state.
      if (!carousel.classList.contains('product-reviews__carousel--empty')) {
        carousel.style.display = 'block';
      }
      // Keep it hidden initially, will fade in later.
      carousel.classList.remove('is-visible');
    }
  }

  /**
   * Show error state.
   */
  showError() {
    if (this.loadingElement && this.loadingElement instanceof HTMLElement) {
      this.loadingElement.style.display = 'none';
    }
    if (this.errorElement && this.errorElement instanceof HTMLElement) {
      this.errorElement.style.display = 'block';
    }
    if (this.emptyElement && this.emptyElement instanceof HTMLElement) {
      this.emptyElement.style.display = 'none';
    }

    // Hide the overall rating and carousel when there's an error.
    const overallRating = document.querySelector('.js-reviews-overall');
    if (overallRating && overallRating instanceof HTMLElement) {
      overallRating.style.display = 'none';
    }

    const carousel = document.querySelector('.js-reviews-carousel');
    if (carousel && carousel instanceof HTMLElement) {
      carousel.style.display = 'none';
    }

    // Center the error message in the carousel wrapper.
    const carouselWrapper = document.querySelector('.js-reviews-carousel-wrapper');
    if (carouselWrapper && carouselWrapper instanceof HTMLElement) {
      carouselWrapper.style.justifyContent = 'center';
      carouselWrapper.style.alignItems = 'center';
      carouselWrapper.style.textAlign = 'center';
    }
  }

  /**
   * Show empty state.
   */
  showEmpty() {
    if (this.loadingElement && this.loadingElement instanceof HTMLElement) {
      this.loadingElement.style.display = 'none';
    }
    if (this.errorElement && this.errorElement instanceof HTMLElement) {
      this.errorElement.style.display = 'none';
    }
    if (this.emptyElement && this.emptyElement instanceof HTMLElement) {
      this.emptyElement.style.display = 'block';
    }

    // Add --empty class to carousel when there are no reviews.
    const carousel = document.querySelector('.js-reviews-carousel');
    if (carousel && carousel instanceof HTMLElement) {
      carousel.classList.add('product-reviews__carousel--empty');
      // Remove any inline display style to allow CSS to take precedence.
      carousel.style.display = '';
    }

    // Check if we have a minimum rating filter and update the message accordingly.
    if (this.config && this.config.min_rating && this.config.min_rating > 1) {
      const emptyMessage = this.emptyElement?.querySelector('h3');
      if (emptyMessage && emptyMessage instanceof HTMLElement) {
        // Get the actual product title from the hidden product info section.
        const productTitleElement = document.querySelector('.js-reviews-product-title');
        const productTitle = productTitleElement ? productTitleElement.textContent : 'This product';

        // Get the customisable message from the section settings.
        const noReviewsMessage = this.config.no_reviews_message || 'has no reviews - be the first!';

        // Replace the {rating} placeholder with the actual minimum rating.
        const formattedMessage = noReviewsMessage.replace('{rating}', `${this.config.min_rating}+ stars`);

        emptyMessage.textContent = `${productTitle} ${formattedMessage}`;
      }
    }

    // Hide the overall rating column when there are no reviews.
    const overallRating = document.querySelector('.js-reviews-overall');
    if (overallRating && overallRating instanceof HTMLElement) {
      overallRating.style.display = 'none';
    }
  }

  /**
   * Helper method to trigger fade-in animations for elements.
   */
  fadeInElements() {
    // First, completely hide the loading state.
    if (this.loadingElement && this.loadingElement instanceof HTMLElement) {
      this.loadingElement.style.display = 'none';
      this.loadingElement.style.position = 'absolute';
      this.loadingElement.style.visibility = 'hidden';
    }

    // Adjust the carousel wrapper layout when loading is hidden.
    const carouselWrapper = document.querySelector('.js-reviews-carousel-wrapper');
    if (carouselWrapper && carouselWrapper instanceof HTMLElement) {
      carouselWrapper.style.justifyContent = 'flex-start';
    }

    // Wait a brief moment for the loading state to disappear, then trigger fade-ins.
    setTimeout(() => {
      const overallRating = document.querySelector('.js-reviews-overall');
      if (overallRating && overallRating instanceof HTMLElement) {
        overallRating.classList.add('is-visible');
      }

      const carousel = document.querySelector('.js-reviews-carousel');
      if (carousel && carousel instanceof HTMLElement) {
        // Remove --empty class when reviews are successfully loaded.
        carousel.classList.remove('product-reviews__carousel--empty');
        // Ensure display is set to block when showing reviews.
        carousel.style.display = 'block';
        carousel.classList.add('is-visible');
      }
    }, 100);
  }

  /**
   * Open the image lightbox.
   * @param {string} imageSrc - The source URL of the image to display.
   * @param {string} imageAlt - The alt text for the image.
   */
  openLightbox(imageSrc, imageAlt) {
    if (!this.lightbox) {
      console.error('ProductReviews: Lightbox element not found');
      return;
    }

    const lightboxImage = this.lightbox.querySelector('.js-lightbox-image');
    if (lightboxImage && lightboxImage instanceof HTMLImageElement) {
      lightboxImage.src = imageSrc;
      lightboxImage.alt = imageAlt || 'Review image';
    }

    if (this.lightbox instanceof HTMLElement) {
      this.lightbox.style.display = 'flex';
    }
    document.body.style.overflow = 'hidden';
  }

  /**
   * Close the image lightbox.
   */
  closeLightbox() {
    if (!this.lightbox) return;

    if (this.lightbox instanceof HTMLElement) {
      this.lightbox.style.display = 'none';
    }
    document.body.style.overflow = '';
  }

  /**
   * Initialise lightbox event listeners.
   */
  initLightbox() {
    if (!this.lightbox) return;

    // Close button click.
    const closeButton = this.lightbox.querySelector('.js-lightbox-close');
    if (closeButton) {
      closeButton.addEventListener('click', () => this.closeLightbox());
    }

    // Lightbox container click (to close when clicking background).
    this.lightbox.addEventListener('click', (event) => {
      // Only close if clicking the lightbox container itself, not its children.
      if (event.target === this.lightbox) {
        this.closeLightbox();
      }
    });

    // Escape key.
    document.addEventListener('keydown', (event) => {
      if (
        event.key === 'Escape' &&
        this.lightbox &&
        this.lightbox instanceof HTMLElement &&
        this.lightbox.style.display === 'flex'
      ) {
        this.closeLightbox();
      }
    });

    // Review image clicks (delegated event listener).
    document.addEventListener('click', (event) => {
      if (event.target && event.target instanceof Element) {
        const image = event.target.closest('.js-lightbox-trigger');
        if (image && image instanceof HTMLElement) {
          event.preventDefault();
          const imageSrc = image.dataset.imageSrc;
          const imageAlt = image.dataset.imageAlt;
          if (imageSrc && typeof imageSrc === 'string') {
            this.openLightbox(imageSrc, imageAlt || 'Review image');
          }
        }
      }
    });
  }

  /**
   * Add show/hide functionality for long content.
   * @param {HTMLElement} reviewContent - The review content element.
   * @param {HTMLElement} contentSection - The content section element that contains the review content and toggle.
   */
  addShowHideToggle(reviewContent, contentSection) {
    // Measure the actual height of the content without truncation.
    const originalHeight = reviewContent.offsetHeight;

    // Calculate the height of 4 lines by getting the line height.
    const computedStyle = window.getComputedStyle(reviewContent);
    const lineHeight = parseFloat(computedStyle.lineHeight) || parseFloat(computedStyle.fontSize) * 1.4;
    const fourLinesHeight = lineHeight * 4;

    // Only add toggle if content actually exceeds 4 lines (with some tolerance).
    if (originalHeight > fourLinesHeight + 10) {
      // Apply truncation.
      reviewContent.classList.add('is-truncated');

      // Create toggle button.
      const toggleButton = document.createElement('button');
      toggleButton.className = 'product-reviews__content-toggle button button--tertiary button--black';
      toggleButton.textContent = 'Show more';
      toggleButton.setAttribute('aria-expanded', 'false');
      toggleButton.setAttribute('aria-label', 'Show full review content');

      let isExpanded = false;

      toggleButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();

        isExpanded = !isExpanded;

        if (isExpanded) {
          reviewContent.classList.remove('is-truncated');
          toggleButton.textContent = 'Show less';
          toggleButton.setAttribute('aria-expanded', 'true');
          toggleButton.setAttribute('aria-label', 'Show less review content');
        } else {
          reviewContent.classList.add('is-truncated');
          toggleButton.textContent = 'Show more';
          toggleButton.setAttribute('aria-expanded', 'false');
          toggleButton.setAttribute('aria-label', 'Show full review content');
        }
      });

      // Insert the toggle button right after the review content.
      contentSection.appendChild(toggleButton);
    }
  }
}

// Initialise when DOM is ready.
const initializeProductReviews = () => {
  new ProductReviews();
};

// Initialize based on DOM state.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeProductReviews);
} else {
  initializeProductReviews();
}

export default ProductReviews;
